import { useState, useEffect, useContext } from 'react';
import * as Sentry from '@sentry/react';
import { useInfiniteQuery } from 'react-query';
import { PageMetadata } from '../components/datagridComponents/paginationHelpers';
import { DEFAULT_PAGE_SIZE, LOCATIONS_ORGS_API_CACHE_KEY } from '../constants';
import { skipSentry } from '../helpers/helpers';
import { LocationV1, SelectOption } from '../types';
import { ApiContext } from '../context/ApiContext';
import { API_V1_HOSTNAME_KEY } from '../localStorageKeys';

const useLocations = () => {
  const { apiFetch } = useContext(ApiContext);
  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [allLocationsValues, setAllLocationsValues] = useState<LocationV1[]>([]);

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    refetch: fetchLocationOptions,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<{ data: LocationV1[]; metadata: PageMetadata }>({
    queryKey: [LOCATIONS_ORGS_API_CACHE_KEY],
    queryFn: ({ pageParam = 1 }) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/location?limit=${DEFAULT_PAGE_SIZE}&page=${pageParam}`
      ),
    getNextPageParam: (lastPage) => {
      if (
        lastPage?.metadata &&
        lastPage?.metadata.page &&
        lastPage?.metadata.totalPages &&
        lastPage.metadata.page < lastPage.metadata.totalPages
      ) {
        return lastPage.metadata.page + 1;
      }

      return null;
    },
    onError: (err: any) => {
      if (err?.code === 401) return;
      if (!skipSentry(err)) {
        Sentry.captureException(new Error('Error fetching Locations'), {
          extra: { error: JSON.stringify(err) },
        });
      }
      setIsLoadingLocations(false);
    },
  });

  useEffect(() => {
    if (isLoading) {
      setIsLoadingLocations(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!data) return;

    if (hasNextPage) {
      fetchNextPage();
    } else {
      let locationsOptions: SelectOption[] = [];
      let allLocations: LocationV1[] = [];

      if (data.pages && Array.isArray(data.pages)) {
        for (let i = 0; i < data.pages.length; i += 1) {
          const pageData = data.pages[i]?.data || [];

          locationsOptions = locationsOptions.concat(
            pageData.map((location) => ({
              label: location.name,
              value: location.id.toString(),
            }))
          );

          allLocations = allLocations.concat(pageData);
        }
      }

      setOptions(locationsOptions);
      setAllLocationsValues(allLocations);
      setIsLoadingLocations(false);
    }
  }, [data]);

  return {
    locations: options,
    allLocationsValues,
    isLoading: isLoading || isLoadingLocations,
    isRefetching,
    isError,
    fetchLocationOptions,
  };
};

export default useLocations;
